import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Save,
  ShieldCheck,
} from "lucide-react";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useQuery } from "react-query";
import { useAuth } from "../hooks/useAuth";
import { apiService } from "../services/api";
import {
  type AdvisoryForm,
  csafToForm,
  emptyForm,
  formToCsaf,
} from "../utils/csafForm";
import { printCsafPdf } from "../utils/csafPdf";
import { validateCsaf } from "../utils/csafValidator";
import AdvisoryFormEditor from "./AdvisoryFormEditor";

export interface AdvisoryEditorSubmit {
  title: string;
  status: "draft" | "final";
  businessArea: string;
  division: string;
  csaf: Record<string, unknown>;
}

interface Props {
  mode: "create" | "edit";
  initialCsaf?: Record<string, unknown>;
  initialBusinessArea?: string;
  initialDivision?: string;
  submitting: boolean;
  onSubmit: (payload: AdvisoryEditorSubmit) => void;
}

export default function AdvisoryEditor({
  mode,
  initialCsaf,
  initialBusinessArea,
  initialDivision,
  submitting,
  onSubmit,
}: Props) {
  const { user } = useAuth();

  const [businessArea, setBusinessArea] = useState(
    initialBusinessArea ?? user?.businessArea ?? "",
  );
  const [division, setDivision] = useState(initialDivision ?? "");
  const [form, setForm] = useState<AdvisoryForm>(() =>
    initialCsaf ? csafToForm(initialCsaf) : emptyForm(),
  );
  const [csafText, setCsafText] = useState(() =>
    JSON.stringify(initialCsaf ?? formToCsaf(emptyForm()), null, 2),
  );
  const [editorMode, setEditorMode] = useState<"form" | "json">("form");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [csafValid, setCsafValid] = useState(false);

  const { data: businessAreasResponse } = useQuery(
    ["business-areas"],
    apiService.getBusinessAreas,
  );

  const { data: divisionsResponse } = useQuery(
    ["divisions", businessArea],
    () => apiService.getDivisions(businessArea),
    { enabled: !!businessArea },
  );

  const businessAreas: string[] =
    (businessAreasResponse?.data?.data as string[] | undefined) || [];
  const divisions: string[] =
    (divisionsResponse?.data?.data as string[] | undefined) || [];

  const parsedCsafMeta = useMemo(() => {
    try {
      return JSON.parse(csafText) as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [csafText]);

  const derivedTitle = useMemo(() => {
    const doc = parsedCsafMeta?.document as Record<string, unknown> | undefined;
    return typeof doc?.title === "string" ? doc.title : "";
  }, [parsedCsafMeta]);

  const derivedStatus: "draft" | "final" = useMemo(() => {
    const doc = parsedCsafMeta?.document as Record<string, unknown> | undefined;
    const tracking = doc?.tracking as Record<string, unknown> | undefined;
    return tracking?.status === "final" ? "final" : "draft";
  }, [parsedCsafMeta]);

  const canSubmit = useMemo(() => {
    return !!derivedTitle.trim() && !!businessArea && !!division.trim();
  }, [derivedTitle, businessArea, division]);

  // In Form mode the guided form is authoritative: regenerate CSAF JSON live.
  const applyForm = (next: AdvisoryForm) => {
    setForm(next);
    setCsafText(JSON.stringify(formToCsaf(next), null, 2));
    setCsafValid(false);
    setValidationErrors([]);
  };

  const switchMode = (next: "form" | "json") => {
    if (next === editorMode) return;
    if (next === "form") {
      try {
        setForm(csafToForm(JSON.parse(csafText) as Record<string, unknown>));
      } catch {
        toast.error("CSAF JSON is invalid — fix it before using the form");
        return;
      }
    }
    setEditorMode(next);
  };

  const onValidate = (): boolean => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(csafText);
    } catch {
      setCsafValid(false);
      setValidationErrors(["Document is not valid JSON."]);
      toast.error("CSAF JSON is invalid");
      return false;
    }

    const result = validateCsaf(parsed);
    setCsafValid(result.valid);
    setValidationErrors(result.errors);
    if (result.valid) {
      toast.success("Valid CSAF 2.0 document");
    } else {
      toast.error(
        `CSAF invalid: ${result.errors.length} issue${
          result.errors.length === 1 ? "" : "s"
        }`,
      );
    }
    return result.valid;
  };

  const onExportPdf = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(csafText);
    } catch {
      setCsafValid(false);
      setValidationErrors(["Document is not valid JSON."]);
      toast.error("CSAF JSON is invalid — cannot render PDF");
      return;
    }

    const result = validateCsaf(parsed);
    setCsafValid(result.valid);
    setValidationErrors(result.errors);
    if (!result.valid) {
      toast.error(
        `Fix ${result.errors.length} CSAF validation issue${
          result.errors.length === 1 ? "" : "s"
        } before exporting a PDF`,
      );
      return;
    }

    if (!printCsafPdf(parsed)) {
      toast.error("Unable to open the PDF print view");
    }
  };

  const handleSave = () => {
    let parsedCsaf: Record<string, unknown>;
    try {
      parsedCsaf = JSON.parse(csafText);
    } catch {
      setCsafValid(false);
      setValidationErrors(["Document is not valid JSON."]);
      toast.error("CSAF JSON is invalid");
      return;
    }

    const result = validateCsaf(parsedCsaf);
    setCsafValid(result.valid);
    setValidationErrors(result.errors);
    if (!result.valid) {
      toast.error(
        `Fix ${result.errors.length} CSAF validation issue${
          result.errors.length === 1 ? "" : "s"
        } before saving`,
      );
      return;
    }

    onSubmit({
      title: derivedTitle.trim() || "Untitled advisory",
      status: derivedStatus,
      businessArea,
      division: division.trim(),
      csaf: parsedCsaf,
    });
  };

  return (
    <div className="card p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Business area
          </label>
          <select
            className="input w-full"
            value={businessArea}
            onChange={(e) => {
              setBusinessArea(e.target.value);
              setDivision("");
            }}
          >
            <option value="">Select business area</option>
            {businessAreas.map((ba) => (
              <option key={ba} value={ba}>
                {ba}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Division/Business Unit
          </label>
          <select
            className="input w-full"
            value={division}
            onChange={(e) => setDivision(e.target.value)}
          >
            <option value="">Select division/business unit</option>
            {divisions.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
            <button
              type="button"
              onClick={() => switchMode("form")}
              className={`px-3 py-1.5 text-sm rounded-md ${
                editorMode === "form"
                  ? "bg-primary-600 text-white"
                  : "text-gray-600 dark:text-gray-300"
              }`}
            >
              Guided form
            </button>
            <button
              type="button"
              onClick={() => switchMode("json")}
              className={`px-3 py-1.5 text-sm rounded-md ${
                editorMode === "json"
                  ? "bg-primary-600 text-white"
                  : "text-gray-600 dark:text-gray-300"
              }`}
            >
              CSAF JSON
            </button>
          </div>
          {csafValid && validationErrors.length === 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-300">
              <CheckCircle2 className="h-4 w-4" />
              Valid CSAF 2.0
            </span>
          )}
        </div>

        {editorMode === "form" ? (
          <AdvisoryFormEditor value={form} onChange={applyForm} />
        ) : (
          <textarea
            className="input w-full font-mono text-xs resize-y min-h-[28rem]"
            rows={30}
            spellCheck={false}
            value={csafText}
            onChange={(e) => {
              setCsafText(e.target.value);
              setCsafValid(false);
            }}
          />
        )}
      </div>

      {validationErrors.length > 0 && (
        <div className="rounded-md border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-200">
            <AlertTriangle className="h-4 w-4" />
            {validationErrors.length} CSAF validation issue
            {validationErrors.length === 1 ? "" : "s"}
          </div>
          <ul className="mt-2 space-y-1 text-xs text-red-700 dark:text-red-300 list-disc list-inside max-h-40 overflow-auto">
            {validationErrors.slice(0, 20).map((err) => (
              <li key={err}>{err}</li>
            ))}
            {validationErrors.length > 20 && (
              <li>…and {validationErrors.length - 20} more</li>
            )}
          </ul>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          className="btn btn-secondary"
          onClick={onValidate}
          type="button"
        >
          <ShieldCheck className="h-4 w-4 mr-2" />
          Validate CSAF
        </button>
        <button
          className="btn btn-secondary"
          onClick={onExportPdf}
          type="button"
        >
          <FileText className="h-4 w-4 mr-2" />
          Export PDF
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={!canSubmit || submitting}
          type="button"
        >
          <Save className="h-4 w-4 mr-2" />
          {mode === "edit" ? "Update advisory" : "Save advisory"}
        </button>
      </div>
    </div>
  );
}
